import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  useTheme,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Stack,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  Refresh,
  Add,
  ArrowDownward,
  CheckCircle,
  Warning,
  AccountBalance,
  Receipt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { UserContext } from '../../components/shared/useUser';
import {
  getCashTransactionClient,
  CashReconciliationDto,
  TillStateDto,
  formatCashAmount,
  getReconciliationStatusColor,
} from '../../app/services/api/cashTransactions';
import useLogError from '../../hooks/useLogError';
import { CashReconciliationProvider } from './contexts/CashReconciliationContext';
import OpenTillModal from './modals/OpenTillModal';
import CloseTillModal from './modals/CloseTillModal';
import CashDropModal from './modals/CashDropModal';

const CashReconciliationPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { logError } = useLogError();

  const [loading, setLoading] = useState(false);
  const [tillStates, setTillStates] = useState<TillStateDto[]>([]);
  const [reconciliations, setReconciliations] = useState<CashReconciliationDto[]>([]);
  const [dateFilter, setDateFilter] = useState<string>('month');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openTillModalOpen, setOpenTillModalOpen] = useState(false);
  const [closeTillModalOpen, setCloseTillModalOpen] = useState(false);
  const [cashDropModalOpen, setCashDropModalOpen] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState<CashReconciliationDto | null>(null);

  const cashClient = getCashTransactionClient();

  const hasReadPermission = hasPermissionTo(['CashReconciliationRead']);
  const hasWritePermission = hasPermissionTo(['CashReconciliationWrite']);

  useEffect(() => {
    if (selectedStore?.storeNumber && hasReadPermission) {
      loadData();
    }
  }, [selectedStore?.storeNumber, dateFilter, statusFilter]);

  const loadData = async () => {
    if (!selectedStore?.storeNumber) return;

    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();

      const [tillsData, reconciliationsData] = await Promise.all([
        cashClient.getAllTillStates(selectedStore.storeNumber),
        cashClient.getCashReconciliations(
          selectedStore.storeNumber,
          startDate,
          endDate,
          statusFilter === 'all' ? undefined : statusFilter,
        ),
      ]);

      setTillStates(tillsData);
      setReconciliations(reconciliationsData);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const today = dayjs();
    let startDate: string;
    let endDate: string;

    switch (dateFilter) {
      case 'today':
        startDate = today.format('YYYY-MM-DD');
        endDate = today.format('YYYY-MM-DD');
        break;
      case 'week':
        startDate = today.subtract(7, 'day').format('YYYY-MM-DD');
        endDate = today.format('YYYY-MM-DD');
        break;
      case 'month':
        startDate = today.subtract(30, 'day').format('YYYY-MM-DD');
        endDate = today.format('YYYY-MM-DD');
        break;
      default:
        startDate = today.format('YYYY-MM-DD');
        endDate = today.format('YYYY-MM-DD');
    }

    return { startDate, endDate };
  };

  const handleCloseTill = (reconciliation: CashReconciliationDto) => {
    setSelectedReconciliation(reconciliation);
    setCloseTillModalOpen(true);
  };

  const handleViewDetails = (reconciliationId: number) => {
    navigate(`/cash-reconciliation/${reconciliationId}`);
  };

  const openTillsCount = tillStates.filter(t => t.status === 'Open').length;
  const pendingApprovalsCount = reconciliations.filter(
    r => r.status === 'Submitted' || r.status === 'VarianceUnderReview',
  ).length;
  const totalVariance = reconciliations
    .filter(r => r.status === 'Open' || r.status === 'Counting')
    .reduce((sum, r) => sum + r.variance, 0);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedReconciliations = reconciliations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!hasReadPermission) {
    return (
      <PageArea>
        <Typography>You do not have permission to view cash reconciliations.</Typography>
      </PageArea>
    );
  }

  return (
    <CashReconciliationProvider>
      <PageArea>
        <Grid container
          spacing={5}>
          <Grid item
            xs={12}
            md={9}>
            <PageTitle>Cash Reconciliation</PageTitle>
          </Grid>

          <Grid item
            xs={12}
            md={3}
            sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              size='lg'
              variant='secondary'
              startIcon={<Refresh />}
              onClick={loadData}
            >
              Refresh
            </Button>
            {hasWritePermission && (
              <>
                <Button
                  size='lg'
                  variant='secondary'
                  startIcon={<ArrowDownward />}
                  onClick={() => setCashDropModalOpen(true)}
                >
                  Cash Drop
                </Button>
                <Button
                  size='lg'
                  variant='primary'
                  startIcon={<Add />}
                  onClick={() => setOpenTillModalOpen(true)}
                >
                  Open Till
                </Button>
              </>
            )}
          </Grid>

          {loading && (
            <Grid item
              xs={12}>
              <LinearProgress />
            </Grid>
          )}

          {/* Summary Cards */}
          <Grid item
            xs={12}
            md={4}>
            <Section style={{ height: '100%', bgcolor: theme.palette.info.light + '15' }}>
              <Box sx={{ p: 5 }}>
                <Stack spacing={1}>
                  <Stack direction="row"
                    justifyContent="space-between"
                    alignItems="center">
                    <Typography variant="textSM"
                      color="text.secondary"
                      fontWeight={600}>
                      Open Tills
                    </Typography>
                    <Receipt sx={{ color: theme.palette.info.main }} />
                  </Stack>
                  <Typography variant="displaySM"
                    fontWeight={700}
                    color={theme.palette.info.main}>
                    {openTillsCount}
                  </Typography>
                  <Typography variant="textSM"
                    color="text.secondary">
                    Currently active
                  </Typography>
                </Stack>
              </Box>
            </Section>
          </Grid>

          <Grid item
            xs={12}
            md={4}>
            <Section style={{ height: '100%', bgcolor: theme.palette.warning.light + '15' }}>
              <Box sx={{ p: 5 }}>
                <Stack spacing={1}>
                  <Stack direction="row"
                    justifyContent="space-between"
                    alignItems="center">
                    <Typography variant="textSM"
                      color="text.secondary"
                      fontWeight={600}>
                      Pending Approvals
                    </Typography>
                    <Warning sx={{ color: theme.palette.warning.main }} />
                  </Stack>
                  <Typography variant="displaySM"
                    fontWeight={700}
                    color={theme.palette.warning.main}>
                    {pendingApprovalsCount}
                  </Typography>
                  <Typography variant="textSM"
                    color="text.secondary">
                    Require review
                  </Typography>
                </Stack>
              </Box>
            </Section>
          </Grid>

          <Grid item
            xs={12}
            md={4}>
            <Section style={{ height: '100%' }}>
              <Box sx={{ p: 5 }}>
                <Stack spacing={1}>
                  <Stack direction="row"
                    justifyContent="space-between"
                    alignItems="center">
                    <Typography variant="textSM"
                      color="text.secondary"
                      fontWeight={600}>
                      Total Variance
                    </Typography>
                    <AccountBalance sx={{ color: theme.palette.primary.main }} />
                  </Stack>
                  <Typography
                    variant="displaySM"
                    fontWeight={700}
                    color={totalVariance >= 0 ? 'success.main' : 'error.main'}
                  >
                    {totalVariance >= 0 ? '+' : ''}
                    {formatCashAmount(totalVariance, 'GBP')}
                  </Typography>
                  <Typography variant="textSM"
                    color="text.secondary">
                    Active reconciliations
                  </Typography>
                </Stack>
              </Box>
            </Section>
          </Grid>

          <Grid item
            xs={12}>
            <Section>
              <TableHeader>Till Status</TableHeader>
              <Box sx={{ p: 5, pt: 0 }}>
                <Grid container
                  spacing={2}>
                  {tillStates.map((till) => (
                    <Grid item
                      xs={12}
                      sm={6}
                      md={4}
                      key={till.tillNumber}>
                      <Card variant="outlined"
                        sx={{ height: '100%' }}>
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction="row"
                              justifyContent="space-between"
                              alignItems="center">
                              <Typography variant="textMD"
                                fontWeight={600}>
                                {till.tillNumber}
                              </Typography>
                              <Chip
                                label={till.status}
                                size="small"
                                color={
                                  getReconciliationStatusColor(till.status) as
                                  | 'default'
                                  | 'primary'
                                  | 'secondary'
                                  | 'error'
                                  | 'info'
                                  | 'success'
                                  | 'warning'
                                }
                              />
                            </Stack>
                            <Typography variant="textLG"
                              fontWeight={700}>
                              {formatCashAmount(till.currentBalance, 'GBP')}
                            </Typography>
                            {till.assignedUsername && (
                              <Typography variant="textSM"
                                color="text.secondary">
                                {till.assignedUsername}
                              </Typography>
                            )}
                            {till.status === 'Closed' && till.lastReconciliationTimestamp && (
                              <Typography variant="textXS"
                                color="text.secondary">
                                Last reconciled: {dayjs(till.lastReconciliationTimestamp).format('DD/MM/YYYY HH:mm')}
                              </Typography>
                            )}
                            {till.status === 'Open' && till.lastReconciliationId && hasWritePermission && (
                              <Button
                                size="md"
                                variant="secondary"
                                onClick={() => {
                                  const recon = reconciliations.find(r => r.id === till.lastReconciliationId);
                                  if (recon) handleCloseTill(recon);
                                }}
                              >
                                Close Till
                              </Button>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Section>
          </Grid>

          <Grid item
            xs={12}>
            <Section>
              <Box sx={{ p: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="textMD"
                  fontWeight={600}>
                  Reconciliations
                </Typography>
                <Stack direction="row"
                  spacing={2}>
                  <Select
                    value={dateFilter}
                    onChange={(e: SelectChangeEvent) => setDateFilter(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">Last 7 Days</MenuItem>
                    <MenuItem value="month">Last 30 Days</MenuItem>
                  </Select>
                  <Select
                    value={statusFilter}
                    onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="Open">Open</MenuItem>
                    <MenuItem value="Counting">Counting</MenuItem>
                    <MenuItem value="Submitted">Submitted</MenuItem>
                    <MenuItem value="Reconciled">Reconciled</MenuItem>
                    <MenuItem value="VarianceUnderReview">Variance Review</MenuItem>
                  </Select>
                </Stack>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Till</TableCell>
                      <TableCell>Opened By</TableCell>
                      <TableCell align="right">Opening</TableCell>
                      <TableCell align="right">Expected</TableCell>
                      <TableCell align="right">Declared</TableCell>
                      <TableCell align="right">Variance</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedReconciliations.map((recon) => (
                      <TableRow key={recon.id}
                        hover>
                        <TableCell>
                          <Typography variant="textSM">
                            {dayjs(recon.reconciliationDate).format('DD/MM/YYYY')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textSM"
                            fontWeight={600}>
                            {recon.tillNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textSM">{recon.openedByUsername}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="textSM">
                            {formatCashAmount(recon.openingBalance, 'GBP')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="textSM">
                            {formatCashAmount(recon.expectedClosingBalance, 'GBP')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="textSM">
                            {recon.declaredClosingBalance !== undefined
                              ? formatCashAmount(recon.declaredClosingBalance, 'GBP')
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="textSM"
                            fontWeight={600}
                            color={
                              recon.variance === 0
                                ? 'text.primary'
                                : recon.variance > 0
                                  ? 'success.main'
                                  : 'error.main'
                            }
                          >
                            {recon.variance !== 0 && (recon.variance > 0 ? '+' : '')}
                            {recon.variance !== 0 ? formatCashAmount(recon.variance, 'GBP') : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={recon.status}
                            size="small"
                            color={
                              getReconciliationStatusColor(recon.status) as
                              | 'default'
                              | 'primary'
                              | 'secondary'
                              | 'error'
                              | 'info'
                              | 'success'
                              | 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row"
                            spacing={1}
                            justifyContent="center">
                            <Tooltip title="View Details">
                              <IconButton size="small"
                                onClick={() => handleViewDetails(recon.id)}>
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {reconciliations.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={9}
                          align="center">
                          <Typography variant="textSM"
                            color="text.secondary"
                            py={3}>
                            No reconciliations found for the selected period
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        count={reconciliations.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Section>
          </Grid>
        </Grid>

        {/* Modals */}
        <OpenTillModal
          open={openTillModalOpen}
          onClose={() => setOpenTillModalOpen(false)}
          onSuccess={loadData}
        />

        {selectedReconciliation && (
          <CloseTillModal
            open={closeTillModalOpen}
            reconciliation={selectedReconciliation}
            onClose={() => {
              setCloseTillModalOpen(false);
              setSelectedReconciliation(null);
            }}
            onSuccess={loadData}
          />
        )}

        <CashDropModal
          open={cashDropModalOpen}
          onClose={() => setCashDropModalOpen(false)}
          onSuccess={loadData}
        />
      </PageArea>
    </CashReconciliationProvider>
  );
};

export default CashReconciliationPage;
